import Dexie from 'dexie';

import {
    LOGGER_EVENT_KEYS,
    type SEARCH_CLEAR_REASON,
    SEARCH_TERMINATE_REASON,
} from '../constants';
import { type AppDatabase, db } from '../db';
import { logger } from '../log';
import {
    SEARCH_INDEX_STATUS,
    type SearchIndexStatus,
    useSearchIndexStore,
} from '../store';
import {
    BOOLEAN_VALUE,
    SEARCH_WORKER_REQUEST,
    SEARCH_WORKER_RESPONSE,
    type SearchDoc,
    type SearchWorkerRequest,
    type SearchWorkerRequestPayload,
    type SearchWorkerResponse,
} from '../types';

import type { Item, ItemVariant } from '../models';

const BUILD_BATCH_SIZE = 300;
const REQUEST_TIMEOUT_MS = 15_000;
const HYDRATE_TIMEOUT_MS = 30_000;
const SLOW_QUERY_THRESHOLD_MS = 150;

const REQUEST_TIMEOUTS_MS: Partial<
    Record<SearchWorkerRequestPayload['type'], number>
> = {
    [SEARCH_WORKER_REQUEST.HYDRATE]: HYDRATE_TIMEOUT_MS,
};

type SearchTerminateReason =
    (typeof SEARCH_TERMINATE_REASON)[keyof typeof SEARCH_TERMINATE_REASON];
type SearchClearReason =
    (typeof SEARCH_CLEAR_REASON)[keyof typeof SEARCH_CLEAR_REASON];

const SEARCH_INIT_STAGE = {
    SETUP: 'setup',
    HYDRATE: 'hydrate',
    BUILD: 'build',
} as const;

type SearchInitStage =
    (typeof SEARCH_INIT_STAGE)[keyof typeof SEARCH_INIT_STAGE];

interface PendingRequest {
    resolve: (response: SearchWorkerResponse) => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
}

const stageToEventName = {
    [SEARCH_INIT_STAGE.BUILD]: LOGGER_EVENT_KEYS.SEARCH.BUILD_ERROR,
    [SEARCH_INIT_STAGE.HYDRATE]: LOGGER_EVENT_KEYS.SEARCH.HYDRATE_ERROR,
    [SEARCH_INIT_STAGE.SETUP]: LOGGER_EVENT_KEYS.SEARCH.INITIALIZE_ERROR,
};

export class SearchService {
    constructor(private readonly db: AppDatabase) {}

    async init(setStatus: (status: SearchIndexStatus) => void) {
        if (this.worker) return;

        let stage: SearchInitStage = SEARCH_INIT_STAGE.SETUP;

        try {
            this.setupWorker(setStatus);
            stage = SEARCH_INIT_STAGE.HYDRATE;
            setStatus(SEARCH_INDEX_STATUS.Hydrating);

            const hydrateStartedAt = performance.now();
            const hydrated = await this.sendRequest({
                type: SEARCH_WORKER_REQUEST.HYDRATE,
                serializedIndex: null,
            });

            const activeItemsCount = await this.getActiveItemsCount();
            const hydratedResult =
                hydrated.type === SEARCH_WORKER_RESPONSE.HYDRATED
                    ? hydrated
                    : null;
            const documentCount = hydratedResult?.documentCount ?? 0;

            logger.info(LOGGER_EVENT_KEYS.SEARCH.HYDRATE_SUCCESS, {
                documentCount,
                activeItemsCount,
                durationMs: Math.round(performance.now() - hydrateStartedAt),
                readMs: hydratedResult?.readMs,
                deserializeMs: hydratedResult?.deserializeMs,
            });

            if (hydratedResult && documentCount === activeItemsCount) {
                setStatus(SEARCH_INDEX_STATUS.Ready);

                return;
            }

            const resumeOffset = hydratedResult?.buildOffset ?? 0;

            logger.info(LOGGER_EVENT_KEYS.SEARCH.HYDRATE_MISMATCH, {
                documentCount,
                activeItemsCount,
                resumeOffset,
            });

            stage = SEARCH_INIT_STAGE.BUILD;
            await this.buildIndex(setStatus, resumeOffset);
        } catch (error) {
            const eventName = stageToEventName[stage];

            logger.error(eventName, {
                stage,
                code: error instanceof Error ? error.name : undefined,
                message: error instanceof Error ? error.message : String(error),
            });
            setStatus(SEARCH_INDEX_STATUS.Error);
            this.terminateWorker(SEARCH_TERMINATE_REASON.INIT_FAILED);
        }
    }

    async query(term: string, categoryIds: string[] = []): Promise<string[]> {
        if (!this.worker) return [];

        const startedAt = performance.now();
        let response: SearchWorkerResponse;

        try {
            response = await this.sendRequest({
                type: SEARCH_WORKER_REQUEST.QUERY,
                term,
                categoryIds,
            });
        } catch (error) {
            logger.error(LOGGER_EVENT_KEYS.SEARCH.QUERY_ERROR, {
                termLength: term.length,
                code: error instanceof Error ? error.name : undefined,
                message: error instanceof Error ? error.message : String(error),
            });

            return [];
        }

        if (response.type !== SEARCH_WORKER_RESPONSE.QUERY_RESULT) return [];

        const durationMs = Math.round(performance.now() - startedAt);
        const hasCategoryFilter = categoryIds.length > 0;

        logger.info(LOGGER_EVENT_KEYS.SEARCH.QUERY_PERFORMANCE, {
            termLength: term.length,
            hasCategoryFilter,
            resultCount: response.ids.length,
            durationMs,
            tierDurationsMs: response.tierDurationsMs,
            tierResultCounts: response.tierResultCounts,
        });

        if (!response.ids.length) {
            logger.warn(LOGGER_EVENT_KEYS.SEARCH.QUERY_EMPTY, {
                termLength: term.length,
                hasCategoryFilter,
            });
        }

        if (durationMs > SLOW_QUERY_THRESHOLD_MS) {
            logger.warn(LOGGER_EVENT_KEYS.SEARCH.QUERY_SLOW, {
                durationMs,
                termLength: term.length,
            });
        }

        return response.ids;
    }

    async upsertItems(items: Item[], variants: ItemVariant[] = []) {
        if (!this.worker) return;

        await this.removeInactiveItems(items);

        const docs = this.toActiveDocs(items, variants);
        if (!docs.length) return;

        const startedAt = performance.now();

        await this.sendRequest({ type: SEARCH_WORKER_REQUEST.UPSERT, docs });
        useSearchIndexStore.getState().markUpdated();

        logger.info(LOGGER_EVENT_KEYS.SEARCH.UPSERT_SUCCESS, {
            upsertedCount: docs.length,
            durationMs: Math.round(performance.now() - startedAt),
        });
    }

    async clearIndex(reason: SearchClearReason) {
        if (!this.worker) return;

        const startedAt = performance.now();

        try {
            await this.sendRequest({ type: SEARCH_WORKER_REQUEST.CLEAR });
        } catch (error) {
            logger.error(LOGGER_EVENT_KEYS.SEARCH.CLEAR_ERROR, {
                reason,
                code: error instanceof Error ? error.name : undefined,
                message: error instanceof Error ? error.message : String(error),
            });

            return;
        }

        useSearchIndexStore.getState().markUpdated();

        logger.info(LOGGER_EVENT_KEYS.SEARCH.INDEX_CLEARED, {
            reason,
            durationMs: Math.round(performance.now() - startedAt),
        });
    }

    terminateWorker(reason: SearchTerminateReason) {
        if (!this.worker) return;

        this.worker.terminate();
        this.worker = null;

        logger.info(LOGGER_EVENT_KEYS.SEARCH.WORKER_TERMINATED, { reason });
    }

    private worker: Worker | null = null;
    private readonly pendingRequests = new Map<string, PendingRequest>();
    private requestCounter = 0;

    private setupWorker(setStatus: (status: SearchIndexStatus) => void) {
        this.worker = new Worker(
            new URL('../lib/search/search-worker.ts', import.meta.url),
            { type: 'module' },
        );

        logger.info(LOGGER_EVENT_KEYS.SEARCH.WORKER_REGISTERED);

        this.worker.onmessage = (event: MessageEvent<SearchWorkerResponse>) =>
            this.handleWorkerMessage(event.data);

        this.worker.onerror = (event) => {
            logger.error(LOGGER_EVENT_KEYS.SEARCH.WORKER_ERROR, {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
            });

            setStatus(SEARCH_INDEX_STATUS.Error);
        };
    }

    private async buildIndex(
        setStatus: (status: SearchIndexStatus) => void,
        resumeOffset = 0,
    ) {
        setStatus(SEARCH_INDEX_STATUS.Building);

        const buildStartedAt = performance.now();

        logger.info(LOGGER_EVENT_KEYS.SEARCH.BUILD_STARTED, { resumeOffset });

        let offset = resumeOffset;
        let hasMore = true;
        let batchesProcessed = 0;
        let documentCount = 0;

        while (hasMore) {
            const items = await this.db.items
                .where('[isActive+nameLower]')
                .between(
                    [BOOLEAN_VALUE.TRUE, Dexie.minKey],
                    [BOOLEAN_VALUE.TRUE, Dexie.maxKey],
                )
                .offset(offset)
                .limit(BUILD_BATCH_SIZE)
                .toArray();

            if (!items.length) break;

            offset += items.length;

            const variants = await this.db.itemVariants
                .where('itemId')
                .anyOf(items.map((item) => item.id))
                .toArray();

            const addBatchResponse = await this.sendRequest({
                type: SEARCH_WORKER_REQUEST.ADD_BATCH,
                docs: this.toActiveDocs(items, variants),
                offset,
            });

            if (
                addBatchResponse.type === SEARCH_WORKER_RESPONSE.ADD_BATCH_DONE
            ) {
                documentCount = addBatchResponse.documentCount;
            }

            batchesProcessed += 1;
            hasMore = items.length === BUILD_BATCH_SIZE;
        }

        await this.sendRequest({ type: SEARCH_WORKER_REQUEST.PERSIST });

        logger.info(LOGGER_EVENT_KEYS.SEARCH.BUILD_SUCCESS, {
            documentCount,
            batchesProcessed,
            durationMs: Math.round(performance.now() - buildStartedAt),
        });

        setStatus(SEARCH_INDEX_STATUS.Ready);
    }

    private async removeInactiveItems(items: Item[]) {
        const itemsToRemove = items
            .filter((item) => item.isActive === BOOLEAN_VALUE.FALSE)
            .map((item) => item.id);

        if (!itemsToRemove.length) return;

        const startedAt = performance.now();

        await this.sendRequest({
            type: SEARCH_WORKER_REQUEST.REMOVE,
            ids: itemsToRemove,
        });

        logger.info(LOGGER_EVENT_KEYS.SEARCH.INDICES_REMOVED, {
            removeCount: itemsToRemove.length,
            durationMs: Math.round(performance.now() - startedAt),
        });
    }

    private async getActiveItemsCount() {
        return this.db.items
            .where('isActive')
            .equals(BOOLEAN_VALUE.TRUE)
            .count();
    }

    private toActiveDocs(items: Item[], variants: ItemVariant[]): SearchDoc[] {
        const variantsByItemId = new Map<string, ItemVariant[]>();

        for (const variant of variants) {
            const existing = variantsByItemId.get(variant.itemId) ?? [];
            existing.push(variant);
            variantsByItemId.set(variant.itemId, existing);
        }

        return items
            .filter((item) => item.isActive === BOOLEAN_VALUE.TRUE)
            .map((item) => ({
                id: item.id,
                name: item.name,
                categoryId: item.categoryId,
                skuBarcodeTokens: this.buildSkuBarcodeTokens(
                    variantsByItemId.get(item.id) ?? [],
                ),
            }));
    }

    private buildSkuBarcodeTokens(variants: ItemVariant[]): string {
        return variants
            .flatMap((variant) => [
                variant.sku,
                variant.barcode,
                ...(variant.barcodes ?? []),
            ])
            .filter((value): value is string => !!value)
            .join(' ');
    }

    private sendRequest(
        request: SearchWorkerRequestPayload,
    ): Promise<SearchWorkerResponse> {
        if (!this.worker) {
            return Promise.reject(
                new Error('Search worker is not initialized'),
            );
        }

        const requestId = String(this.requestCounter++);
        const fullRequest: SearchWorkerRequest = { ...request, requestId };
        const timeoutMs =
            REQUEST_TIMEOUTS_MS[request.type] ?? REQUEST_TIMEOUT_MS;

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(requestId);
                logger.error(LOGGER_EVENT_KEYS.SEARCH.REQUEST_TIMEOUT, {
                    requestType: request.type,
                    timeoutMs,
                });
                reject(
                    new Error(
                        `Search worker request timed out: ${request.type}`,
                    ),
                );
            }, timeoutMs);

            this.pendingRequests.set(requestId, { resolve, reject, timeout });
            this.worker?.postMessage(fullRequest);
        });
    }

    private handleWorkerMessage(response: SearchWorkerResponse) {
        logger.info(LOGGER_EVENT_KEYS.SEARCH.RESPONSE_RECEIVED, {
            response,
        });
        const pending = this.pendingRequests.get(response.requestId);
        if (!pending) return;

        this.pendingRequests.delete(response.requestId);
        clearTimeout(pending.timeout);

        if (response.type === SEARCH_WORKER_RESPONSE.ERROR) {
            pending.reject(new Error(response.message));
            return;
        }

        pending.resolve(response);
    }
}

export const searchService = new SearchService(db);
